/**
 * Minecraft-style breaking overlay: progressive crack textures drawn
 * over the block being mined (5 stages, procedural pixel-art cracks).
 */

import * as THREE from "three";
import type { BreakProgress } from "../systems/block-interaction";

const STAGES = 5;

function crackTexture(stage: number): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 16;
  canvas.height = 16;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, 16, 16);
  ctx.strokeStyle = "rgba(20, 20, 20, 0.85)";
  ctx.lineWidth = 1;

  // Deterministic pseudo-random cracks radiating from the center,
  // more and longer at each stage
  let seed = 7 + stage * 13;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };

  const cracks = 2 + stage * 2;
  for (let i = 0; i < cracks; i++) {
    let x = 8 + (rand() - 0.5) * 4;
    let y = 8 + (rand() - 0.5) * 4;
    ctx.beginPath();
    ctx.moveTo(x, y);
    const segments = 2 + Math.floor(rand() * 2) + stage;
    for (let s = 0; s < segments; s++) {
      x += (rand() - 0.5) * 7;
      y += (rand() - 0.5) * 7;
      ctx.lineTo(Math.max(0, Math.min(16, x)), Math.max(0, Math.min(16, y)));
    }
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  return texture;
}

export type BlockCracks = {
  update: (progress: BreakProgress | null) => void;
  dispose: () => void;
};

export function createBlockCracks(scene: THREE.Scene): BlockCracks {
  const textures: THREE.CanvasTexture[] = [];
  const materials: THREE.MeshBasicMaterial[] = [];
  for (let i = 0; i < STAGES; i++) {
    const texture = crackTexture(i);
    textures.push(texture);
    materials.push(
      new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
      })
    );
  }

  // Slightly larger than a block to avoid z-fighting
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(1.005, 1.005, 1.005), materials[0]);
  mesh.visible = false;
  scene.add(mesh);

  function update(progress: BreakProgress | null) {
    if (!progress || progress.progress <= 0) {
      mesh.visible = false;
      return;
    }
    const stage = Math.min(STAGES - 1, Math.floor(progress.progress * STAGES));
    mesh.material = materials[stage];
    mesh.position.set(progress.x + 0.5, progress.y + 0.5, progress.z + 0.5);
    mesh.visible = true;
  }

  function dispose() {
    scene.remove(mesh);
    mesh.geometry.dispose();
    for (const material of materials) material.dispose();
    for (const texture of textures) texture.dispose();
  }

  return { update, dispose };
}
